<template>
  <v-container
    v-if="authenticationMixin.checkedLogin"
    class="content pa-0"
    fluid
  >
    <!-- Hero section -->
    <page-hero
      title="Kostenoverzicht"
      intro="Overzicht van alle kosten verbonden aan de uitvaart."
    />

    <!-- Cost overview -->
    <v-row justify="center">
      <v-col
        class="center mb-8 pb-8"
        cols="12"
        md="10"
        lg="10"
      >
        <div
          v-if="listMixin.list.length === 0"
          class="kosten-empty text-center"
        >
          Er zijn momenteel geen kosten om weer te geven.
        </div>

        <div
          v-else
          class="kosten-card"
        >
          <!-- Table header -->
          <div class="kosten-header">
            <div class="kosten-row kosten-row--header">
              <span class="kosten-col kosten-col--product">Product</span>
              <span class="kosten-col kosten-col--number">Aantal</span>
              <span class="kosten-col kosten-col--number">Eenheidsprijs</span>
              <span class="kosten-col kosten-col--number">Excl. BTW</span>
              <span class="kosten-col kosten-col--number kosten-col--hide-mobile">BTW</span>
              <span class="kosten-col kosten-col--number kosten-col--hide-mobile">BTW-bedrag</span>
              <span class="kosten-col kosten-col--number kosten-col--total">Totaal</span>
            </div>
          </div>

          <!-- Table rows -->
          <div class="kosten-body">
            <div
              v-for="(item, index) in listMixin.list"
              class="kosten-row"
              :class="{ 'kosten-row--alt': index % 2 === 1 }"
              :key="index"
            >
              <div class="kosten-col kosten-col--product">
                <span class="kosten-product-name">
                  {{ item.product.naamExtern || item.product.naam }}
                </span>
                <span class="kosten-product-group">
                  {{ item.product.productType.productGroep.naam }} &mdash; {{ item.product.productType.naam }}
                </span>
                <span
                  v-if="item.omschrijving"
                  class="kosten-product-description"
                >
                  {{ item.omschrijving }}
                </span>
              </div>
              <span class="kosten-col kosten-col--number">
                {{ item.aantal ? `${item.aantal}x` : '' }}
              </span>
              <span
                class="kosten-col kosten-col--number"
                v-html="item.eenheidsprijs == null ? '' : FormatMixin_FormatDecimal(item.eenheidsprijs, 2, true, '', '&nbsp;EUR')"
              />
              <span
                class="kosten-col kosten-col--number"
                v-html="FormatMixin_FormatDecimal(item.bedragExclusiefBtw, 2, false, '', '&nbsp;EUR')"
              />
              <span class="kosten-col kosten-col--number kosten-col--hide-mobile">
                {{ BtwPercentage(item) }}
              </span>
              <span
                class="kosten-col kosten-col--number kosten-col--hide-mobile"
                v-html="FormatMixin_FormatDecimal(item.bedragBtw, 2, false, '', '&nbsp;EUR')"
              />
              <span
                class="kosten-col kosten-col--number kosten-col--total"
                v-html="item.bedragInclusiefBtw == null ? '' : FormatMixin_FormatDecimal(item.bedragInclusiefBtw, 2, true, '', '&nbsp;EUR')"
              />
            </div>
          </div>

          <!-- Table footer / total -->
          <div class="kosten-footer">
            <div class="kosten-row kosten-row--footer">
              <span class="kosten-col kosten-col--product">
                Totaal
              </span>
              <span class="kosten-col kosten-col--number" />
              <span class="kosten-col kosten-col--number" />
              <span class="kosten-col kosten-col--number" />
              <span class="kosten-col kosten-col--number kosten-col--hide-mobile" />
              <span class="kosten-col kosten-col--number kosten-col--hide-mobile" />
              <span
                class="kosten-col kosten-col--number kosten-col--total"
                v-html="FormatMixin_FormatDecimal(TotaalInclusiefBTW, 2, false, '', '&nbsp;EUR')"
              />
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PageHero from '@/components/PageHero.vue'
import authenticationMixin from '@/mixins/authenticationMixin'
import commonMixin from '@/mixins/commonMixin'
import errorMixin from '@/mixins/errorMixin'
import formatMixin from '@/mixins/formatMixin'
import listMixin from '@/mixins/listMixin'
import klantService from '@/services/klantService'

export default {
  name: 'FacturatieComponent',
  components: { PageHero },
  mixins: [
    authenticationMixin,
    commonMixin,
    errorMixin,
    formatMixin,
    listMixin
  ],
  mounted () {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    this.AuthenticationMixin_CheckIfLoggedIn()
    this.$store.state.headers.raadplegen = true
    klantService.Init(this)
    this.GetUitvaartFacturatieList()
  },
  beforeUnmount () {
    this.$store.state.headers.raadplegen = false
  },
  computed: {
    TotaalInclusiefBTW () {
      const prijzen = this.listMixin.list.length ? this.listMixin.list.map(x => x.bedragInclusiefBtw) : [0]
      return prijzen.reduce((x, y) => x + y) ?? 0
    }
  },
  methods: {
    BtwPercentage (item) {
      if (item.btwPercentageCode && item.btwPercentageCode.longDescription && !isNaN(item.btwPercentageCode.longDescription.replace(',', '.'))) {
        const value = this.FormatMixin_FormatDecimal(parseFloat(item.btwPercentageCode.longDescription), 2, false)
        return value ? `${value}%` : null
      }
      return null
    },
    async GetUitvaartFacturatieList () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetUitvaartFacturatieList()

      if (resp && (resp.data || resp.data !== '')) {
        this.listMixin.list = resp.data
      }

      this.CommonMixin_Load(this.$options.name, false)
    }
  }
}
</script>

<style scoped>
.kosten-empty {
  font-size: 16px;
  color: #999;
  padding: 48px 0;
}

.kosten-card {
  border: 1px solid #e8e4dc;
  border-radius: 10px;
  overflow: hidden;
  background-color: #fdfcfa;
}

/* Row layout */
.kosten-row {
  display: flex;
  align-items: stretch;
  padding: 12px 20px;
  gap: 8px;
}

.kosten-row--header {
  background-color: #424E58;
  color: white;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 14px 20px;
}

.kosten-row--alt {
  background-color: #FBF7EE;
}

.kosten-row--footer {
  background-color: #424E58;
  color: white;
  font-weight: 600;
  font-size: 16px;
  padding: 16px 20px;
}

/* Columns */
.kosten-col {
  display: flex;
  align-items: center;
}

.kosten-col--product {
  flex: 2;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.kosten-col--number {
  flex: 1;
  justify-content: flex-end;
  text-align: right;
  font-size: 14px;
}

.kosten-col--total {
  font-weight: 600;
}

/* Product info */
.kosten-product-name {
  font-size: 15px;
  font-weight: 500;
  color: #424E58;
}

.kosten-product-group {
  font-size: 13px;
  color: #999;
}

.kosten-product-description {
  font-size: 13px;
  color: #6b6b6b;
  font-style: italic;
  margin-top: 2px;
}

/* Responsive */
@media (max-width: 960px) {
  .kosten-col--hide-mobile {
    display: none;
  }

  .kosten-row {
    padding: 10px 12px;
  }
}
</style>
